import { useState } from "react";
import { catches } from "@/mocks/catches";

export default function Products() {
  const [lightbox, setLightbox] = useState<string | null>(null);

  const openLightbox = (id: string) => setLightbox(id);
  const closeLightbox = () => setLightbox(null);

  const activeItem = catches.find((c) => c.id === lightbox);

  const labelColorClasses = [
    "bg-primary-500",
    "bg-accent-500",
    "bg-secondary-500",
  ];

  return (
    <section
      id="products"
      className="relative w-full py-24 md:py-32 bg-background-100 border-t border-background-200/60"
    >
      <div className="w-full px-6 md:px-10 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-[2px] bg-primary-500"></span>
              <span
                className="text-primary-500 text-xs tracking-[0.4em]"
                style={{ fontFamily: "var(--font-label)" }}
              >
                CATCH REPORT
              </span>
            </div>
            <h4
              className="text-foreground-950 leading-[0.9]"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              <a
                href="#products"
                className="hover:text-primary-500 transition-colors cursor-pointer"
              >
                <span className="block text-5xl md:text-7xl">実釣の成果</span>
                <span className="block text-3xl md:text-5xl italic text-primary-500 mt-2">
                  Real Catches.
                </span>
              </a>
            </h4>
          </div>
          <p className="max-w-md text-foreground-600 text-sm md:text-base leading-relaxed">
            雷撃カゴが導き出した、フィールドでの実績。すべて実際の釣果写真です。
          </p>
        </div>

        {/* Catch Gallery Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {catches.map((item, index) => {
            const labelClass = labelColorClasses[index % 3];
            const titleSize =
              index === 0 ? "text-2xl md:text-3xl" : "text-xl md:text-2xl";
            return (
              <button
                key={item.id}
                onClick={() => openLightbox(item.id)}
                className="group relative overflow-hidden rounded-lg cursor-pointer text-left"
              >
                <div className="relative w-full aspect-[16/10] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.alt}
                    title={`${item.fish} ${item.size} / ${item.location} / ${item.lure}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    style={{ objectPosition: item.objectPosition }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span
                      className={`text-[10px] tracking-[0.3em] px-3 py-1.5 rounded-full ${labelClass} text-background-50`}
                    >
                      {item.label}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div
                      className={`text-background-50 ${titleSize}`}
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {item.fish} {item.size}
                    </div>
                    <div className="text-background-50/70 text-xs mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span>{item.location}</span>
                      <span>{item.lure}</span>
                      {item.method && <span>{item.method}</span>}
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur text-background-50">
                      <i className="ri-zoom-in-line text-lg"></i>
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom summary */}
        <div className="mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 border-t border-background-200/60 pt-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-trophy-line text-xl"></i>
            </div>
            <div>
              <div
                className="text-foreground-950 text-lg md:text-xl"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                実釣レポート
              </div>
              <div className="text-xs md:text-sm text-foreground-600 mt-1">
                今まで届かなかった未体験のフィールドに、思いも寄らなかった未経験の釣果が待っています。
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && activeItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 md:p-10"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center rounded-full bg-background-50/10 backdrop-blur text-background-50 hover:bg-background-50/20 transition cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <div
            className="relative max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeItem.image}
              alt={activeItem.alt}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <div
                className="text-background-50 text-lg"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {activeItem.fish} {activeItem.size}
              </div>
              <div className="text-background-50/50 text-xs tracking-widest mt-1">
                {activeItem.location} / {activeItem.lure}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}